

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface WithAuthOptions {
  requireAuth?: boolean;
  redirectTo?: string;
  allowedRoles?: string[];
}

export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
  options: WithAuthOptions = {}
) {
  const { 
    requireAuth = true, 
    redirectTo = '/login',
    allowedRoles = []
  } = options;

  return function ProtectedComponent(props: P) {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
      if (loading) return;

      if (requireAuth && !user) {
        navigate(redirectTo, { replace: true });
        return;
      }

      if (!requireAuth && user) {
        navigate('/dashboard', { replace: true });
        return;
      }

      // Check for allowed roles if specified
      if (requireAuth && user && allowedRoles.length > 0) {
        const userRole = user.role?.toLowerCase();
        const isAllowed = allowedRoles.some(role => role.toLowerCase() === userRole);
        
        if (!isAllowed) {
          navigate('/dashboard', { replace: true });
          return;
        }
      }

      setIsReady(true);
    }, [user, loading, navigate, redirectTo, requireAuth, allowedRoles]);

    if (loading || !isReady) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-stone-50">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
            <p className="text-stone-500 text-sm">Loading...</p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
}
